"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { type SignedInUser, type User, userFromClaims } from "../lib/user";

import { migrateLegacySession } from "./legacy-session";
import {
  useMigratedTokenRevocation,
  useLoginMetrics,
  useSessionEndedElsewhere,
  useUnsupportedRoleLogout,
} from "./provider-effects";
import { createSessionActions, type SessionConfig } from "./session-actions";
import { claimsFromSession } from "./session-claims";
import {
  getSessionSnapshot,
  parseSession,
  subscribeToSession,
} from "./session-storage";

import type { SessionEndReason } from "./session-rules";
import type { UserRole } from "../lib/roles";

export interface UpdatedName {
  readonly givenName: string;
  readonly familyName: string;
}

export interface UserContextValue {
  readonly user: User;
  /**
   * The stored access token as-is, possibly expired, for code that needs it synchronously.
   * Prefer getAccessToken, which refreshes it first.
   */
  readonly accessToken: string | undefined;
  getAccessToken(): Promise<string | undefined>;
  endSession(reason: SessionEndReason): Promise<void>;
  /** Shows a name the user just saved until the session's tokens catch up. */
  updateName(name: UpdatedName): void;
}

export interface UserProviderProps {
  /** Read once, on mount. */
  readonly config: SessionConfig;
  readonly roleOverride?: UserRole;
  /** Called after a session ends, from this tab or another, for the app to react. */
  readonly onSessionEnd: (reason: SessionEndReason) => void;
  readonly children: ReactNode;
}

const SIGNED_OUT: User = { status: "signedOut" };

export const UserContext = createContext<UserContextValue | null>(null);

const withName = (
  user: SignedInUser,
  name: (UpdatedName & { readonly userId: string }) | undefined,
): SignedInUser =>
  name?.userId === user.id
    ? {
        ...user,
        givenName: name.givenName,
        familyName: name.familyName,
        displayName: `${name.givenName} ${name.familyName}`,
      }
    : user;

export const UserProvider = ({
  config: configProp,
  roleOverride,
  onSessionEnd,
  children,
}: UserProviderProps): ReactNode => {
  const [config] = useState(configProp);
  // Before the first read, so a migrated session is there on the first render.
  const [migration] = useState(() =>
    typeof window === "undefined" ? {} : migrateLegacySession(),
  );
  const raw = useSyncExternalStore(
    subscribeToSession,
    getSessionSnapshot,
    () => null,
  );
  const session = useMemo(() => parseSession(raw), [raw]);
  const claims = useMemo(
    () => (session ? claimsFromSession(session) : undefined),
    [session],
  );
  const result = useMemo(
    () => (claims ? userFromClaims(claims, { roleOverride }) : SIGNED_OUT),
    [claims, roleOverride],
  );

  // The latest callback, so the actions can be created once.
  const onSessionEndRef = useRef(onSessionEnd);
  useEffect(() => {
    onSessionEndRef.current = onSessionEnd;
  }, [onSessionEnd]);
  const notifySessionEnd = useCallback(
    (reason: SessionEndReason) => onSessionEndRef.current(reason),
    [],
  );

  const markEndingHere = useSessionEndedElsewhere(session, notifySessionEnd);
  const [actions] = useState(() =>
    createSessionActions(config, {
      onSessionEnding: markEndingHere,
      onSessionEnd: notifySessionEnd,
    }),
  );
  const { endSession } = actions;

  const [updatedName, setUpdatedName] = useState<
    (UpdatedName & { readonly userId: string }) | undefined
  >();
  const signedIn = result.status === "signedIn" ? result : undefined;
  const user = useMemo(
    () => (signedIn ? withName(signedIn, updatedName) : SIGNED_OUT),
    [signedIn, updatedName],
  );

  useMigratedTokenRevocation(config, migration);
  useUnsupportedRoleLogout(result, claims?.sub, endSession);
  useLoginMetrics(signedIn);

  const updateName = useCallback(
    (name: UpdatedName) => {
      if (signedIn) setUpdatedName({ ...name, userId: signedIn.id });
    },
    [signedIn],
  );

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      accessToken: session?.accessToken,
      getAccessToken: actions.getAccessToken,
      endSession,
      updateName,
    }),
    [user, session, actions, endSession, updateName],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextValue => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};

/** The signed-in user, for components that only render while someone is signed in. */
export const useSignedInUser = (): SignedInUser => {
  const { user } = useUser();
  if (user.status !== "signedIn") {
    throw new Error("useSignedInUser was called while nobody is signed in");
  }
  return user;
};
